/**
 * ReviewItemCard
 *
 * Generic selectable card shell used in every review list across both wizards.
 * All three review screens (JiraReviewScreen, ReviewStoriesScreen,
 * ReviewTCsScreen) share the same outer card structure:
 *   - 1.5px border, colour driven by selection / warning / error state
 *   - Checkbox on the left
 *   - Flexible content area on the right
 *   - Optional expandable footer strip (test steps, acceptance criteria, etc.)
 *
 * The component is a "dumb" layout shell — it renders children inside the
 * content area and an optional `footer` below the divider line.
 *
 * Props
 * ─────
 * id            any      – Unique key / id passed back to onToggle
 * selected      bool     – Whether this card is currently selected
 * onToggle      fn       – (id) => void  called when checkbox changes
 * disabled      bool     – Disables the checkbox and edit controls
 * hasError      bool     – Turns border red (save-failed state)
 * hasWarning    bool     – Turns border amber (e.g. missing tech layer)
 * children      node     – Main card body content
 * footer        node     – Optional content rendered inside a collapsible
 *                          bottom strip (pass null/undefined to hide it)
 * footerLabel   string   – Text shown on the expand/collapse toggle button
 *                          (e.g. "View Steps (4)" / "Acceptance Criteria")
 * footerExpanded bool    – Controlled expand state (managed by parent)
 * onFooterToggle fn      – () => void  called when the expand button is clicked
 * ariaLabel     string   – aria-label for the checkbox
 *
 * Usage — JiraReviewScreen (test-case card)
 * ─────────────────────────────────────────
 * <ReviewItemCard
 *   id={tc._id}
 *   selected={tc.selected}
 *   onToggle={toggleSelect}
 *   disabled={submitting}
 *   hasError={!!tc.saveError}
 *   hasWarning={missingTechLayer}
 *   ariaLabel={`Select: ${tc.summary}`}
 *   footer={stepsContent}
 *   footerLabel={`${tc.stepsExpanded ? 'Hide' : 'View'} Steps (${tc.testSteps.length})`}
 *   footerExpanded={tc.stepsExpanded}
 *   onFooterToggle={() => toggleSteps(tc._id)}
 * >
 *   {…tc body…}
 * </ReviewItemCard>
 *
 * Usage — ReviewStoriesScreen (story card)
 * ────────────────────────────────────────
 * <ReviewItemCard
 *   id={story.id}
 *   selected={story.selected}
 *   onToggle={(id) => toggle(id)}
 *   ariaLabel={`Select: ${story.title}`}
 * >
 *   {…story body…}
 * </ReviewItemCard>
 */

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Checkbox, Collapse } from '@mui/material';

const ReviewItemCard = ({
  id,
  selected,
  onToggle,
  disabled = false,
  hasError = false,
  hasWarning = false,
  children,
  footer,
  footerLabel,
  footerExpanded = false,
  onFooterToggle,
  ariaLabel,
}) => {
  const borderColor = hasError
    ? 'error.main'
    : hasWarning
    ? 'warning.main'
    : selected
    ? 'primary.main'
    : '#e5e7eb';

  const hasFooter = footer != null;

  return (
    <Box
      sx={{
        border: '1.5px solid',
        borderColor,
        borderRadius: 1.5,
        overflow: 'hidden',
        transition: 'border-color 0.15s, opacity 0.15s',
      }}
    >
      {/* Main body */}
      <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, alignItems: 'flex-start' }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onToggle(id)}
          disabled={disabled}
          inputProps={{ 'aria-label': ariaLabel ?? `Select item ${id}` }}
          sx={{ mt: 0.25, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Box>

      {/* Optional expandable footer */}
      {hasFooter && (
        <>
          {onFooterToggle && (
            <Button
              fullWidth
              size="small"
              onClick={onFooterToggle}
              disabled={disabled}
              endIcon={footerExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={footerExpanded}
              sx={{
                borderTop: '1px solid #f3f4f6',
                borderRadius: 0,
                justifyContent: 'flex-start',
                pl: 5.5,
                py: 0.75,
                fontSize: '12px',
                color: 'primary.main',
                fontWeight: 600,
                bgcolor: 'transparent',
                textTransform: 'none',
                '&:hover': { bgcolor: '#f9fafb' },
              }}
            >
              {footerLabel}
            </Button>
          )}
          <Collapse in={footerExpanded}>
            <Box
              sx={{
                borderTop: '1px solid #f3f4f6',
                bgcolor: '#fafafa',
                px: 2,
                py: 1,
              }}
            >
              {footer}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default ReviewItemCard;